/**
 * Plan benefit lines: the `{ name, value, network }` entries stored in
 * plans.raw_benefits (the Plan Library / Plan Builder Benefits tab), returned
 * by Word & Brown, and cached in cached_plan_rates.benefits.
 *
 * One reader for every quoting surface (Step 5 Plan Selection, the public
 * Benefits Proposal, the proposal PDF), so a field the library editor saves
 * under one name can't show up on one surface and "—" on another (DEV-355).
 */
import { WB_MEDICAL_BENEFIT_FIELDS } from "../constants/wbBenefitFields";

export interface PlanBenefitLine {
  name: string;
  value: string;
  network?: "in" | "out";
}

/** Headline benefits, keyed like the flat fields PublicQuote / the PDF read. */
export interface PlanBenefitSummary {
  deductible?: string;
  outOfPocketMax?: string;
  copay?: string;
  specialist?: string;
  urgentCare?: string;
  preventiveCare?: string;
  emergencyRoom?: string;
  hospital?: string;
  rxGeneric?: string;
  rxBrand?: string;
  rxTier3?: string;
  rxTier4?: string;
  /** DEV-348 */
  lab?: string;
  imagingBasic?: string;
  imagingAdvanced?: string;
}

/**
 * Normalize whatever a source stored into lines:
 *  - an array of `{ name|Name, value|Value, network? }`;
 *  - a W&B benefit-set object `{ BenefitSet1: [...], BenefitSet3: [...] }`
 *    (set 1 = in-network, set 3 = out-of-network).
 * Blank names/values are dropped.
 */
export function normalizeBenefitLines(raw: unknown): PlanBenefitLine[] {
  const out: PlanBenefitLine[] = [];
  const push = (item: any, network?: "in" | "out") => {
    if (!item || typeof item !== "object") return;
    const name = String(item.name ?? item.Name ?? "").trim();
    const value = String(item.value ?? item.Value ?? "").trim();
    if (!name || !value) return;
    const net = item.network === "in" || item.network === "out" ? item.network : network;
    out.push(net ? { name, value, network: net } : { name, value });
  };
  if (Array.isArray(raw)) {
    raw.forEach((item) => push(item));
  } else if (raw && typeof raw === "object") {
    for (const [key, set] of Object.entries(raw as Record<string, unknown>)) {
      if (!Array.isArray(set)) continue;
      const network = key === "BenefitSet1" ? "in" : key === "BenefitSet3" ? "out" : undefined;
      set.forEach((item) => push(item, network));
    }
  }
  return out;
}

const isRx = (n: string) => /\brx\b|prescription|drug/.test(n);

/** Which summary field a benefit line name feeds, if any. `priority` breaks ties (higher wins). */
function classify(rawName: string): { key: keyof PlanBenefitSummary; priority: number } | null {
  const n = rawName.toLowerCase();
  // W&B's medical-only deductible line ("Medical (Individual/Family)") beats a
  // combined "Deductible (Individual/Family)" / "Overall ..." line.
  if (n.startsWith("medical") && n.includes("individual")) return { key: "deductible", priority: 2 };
  if (n.includes("out-of-pocket") || n.includes("out of pocket") || /\boop\b/.test(n)) {
    return { key: "outOfPocketMax", priority: 1 };
  }
  if (n.includes("deductible") && !isRx(n)) return { key: "deductible", priority: 1 };
  if (isRx(n)) {
    // A second Rx level ("Rx Level B – Tier 1", DEV-348) is detail only: it
    // must not overwrite the plan's headline (first-level) Rx values.
    if (/\blevel\s*b\b|second level/.test(n)) return null;
    if (/tier\s*1\b|generic/.test(n)) return { key: "rxGeneric", priority: 1 };
    if (/tier\s*2\b|brand|preferred/.test(n)) return { key: "rxBrand", priority: 1 };
    if (/tier\s*3\b/.test(n)) return { key: "rxTier3", priority: 1 };
    if (/tier\s*4\b|specialty/.test(n)) return { key: "rxTier4", priority: 1 };
    return null;
  }
  if (n.includes("primary care") || /\bpcp\b/.test(n) || (n.includes("office") && n.includes("visit"))) {
    return { key: "copay", priority: 1 };
  }
  if (n.includes("specialist")) return { key: "specialist", priority: 1 };
  if (n.includes("urgent care")) return { key: "urgentCare", priority: 1 };
  if (n.includes("preventive")) return { key: "preventiveCare", priority: 1 };
  if (n.includes("emergency room") || n === "emergency" || n === "er") return { key: "emergencyRoom", priority: 1 };
  if (n.includes("facility fee") && n.includes("hospital")) return { key: "hospital", priority: 1 };
  // DEV-348. SBC's combined "Diagnostic test (x-ray, blood work)" line feeds lab.
  if (n.includes("laboratory") || /\blab\b/.test(n) || n.includes("blood work")) return { key: "lab", priority: 1 };
  if (n.includes("advanced imaging") || /\b(ct|pet|mri)s?\b/.test(n)) return { key: "imagingAdvanced", priority: 1 };
  if (n.includes("imaging") || n.includes("x-ray") || n.includes("xray")) return { key: "imagingBasic", priority: 1 };
  return null;
}

/**
 * Headline values from benefit lines. In-network (or unlabelled) lines win;
 * an out-of-network line only fills a field no in-network line answered.
 * Values are returned as stored ("$2,900 / $5,800", "$30 Copay", "No Charge").
 */
export function summarizeBenefitLines(raw: unknown): PlanBenefitSummary {
  const lines = normalizeBenefitLines(raw);
  const summary: PlanBenefitSummary = {};
  const rank: Partial<Record<keyof PlanBenefitSummary, number>> = {};
  for (const line of lines) {
    const hit = classify(line.name);
    if (!hit) continue;
    // in-network beats out-of-network, then name priority, then first seen.
    const score = (line.network === "out" ? 0 : 10) + hit.priority;
    if ((rank[hit.key] ?? -1) >= score) continue;
    rank[hit.key] = score;
    summary[hit.key] = line.value;
  }
  return summary;
}

/** Canonical library line name for each summary field (see wbBenefitFields.ts). */
const SUMMARY_LINE_NAMES: Record<keyof PlanBenefitSummary, string> = {
  deductible: "Deductible (Individual/Family)",
  outOfPocketMax: "Out-of-Pocket (Individual/Family)",
  copay: "Primary Care Visit",
  specialist: "Specialist Visit",
  urgentCare: "Urgent Care",
  preventiveCare: "Preventive Care/Screening/Immunizations",
  emergencyRoom: "Emergency Room Services",
  hospital: "Facility Fee (Hospital Room)",
  rxGeneric: "Rx – Tier 1",
  rxBrand: "Rx – Tier 2",
  rxTier3: "Rx – Tier 3",
  rxTier4: "Rx – Tier 4",
  lab: "Laboratory Services (Blood Work)",
  imagingBasic: "Basic Imaging (X-Ray)",
  imagingAdvanced: "Advanced Imaging (CT/PET Scans, MRIs)",
};

// Keep the canonical names honest: every one must be a real library field.
const LIBRARY_FIELD_NAMES = new Set(WB_MEDICAL_BENEFIT_FIELDS.flatMap((g) => g.fields.map((f) => f.name)));
for (const name of Object.values(SUMMARY_LINE_NAMES)) {
  if (!LIBRARY_FIELD_NAMES.has(name)) throw new Error(`planBenefitLines: unknown library field "${name}"`);
}

/**
 * The reverse of summarizeBenefitLines: in-network lines named like the
 * library editor's fields. Used to serve legacy plan_benefits rows (already
 * flattened by flattenPlanBenefits) in the same shape as raw_benefits.
 */
export function benefitLinesFromSummary(summary: Record<string, unknown> | null | undefined): PlanBenefitLine[] {
  if (!summary) return [];
  const lines: PlanBenefitLine[] = [];
  for (const [key, name] of Object.entries(SUMMARY_LINE_NAMES)) {
    const value = summary[key];
    if (value == null || String(value).trim() === "") continue;
    lines.push({ name, value: String(value).trim(), network: "in" });
  }
  return lines;
}
